<!--
  Component:   Field Calculation Modal: AI.
  Path:        Field - Calculation - AI.
  Properties:
  - Provider            [Dropdown]
  - Model               [Dropdown]
  - Include Citations   [Checkbox]
  - Question            Text of query to be enquired of AI provider. [Textbox]
  - Fields              List of Fields in Table that can be embedded in the Question.
-->
<template>
  <div
    v-if="calculationData && aiOptions"
    class="ai-chat__wrapper"
  >
    <div>
      <label for="aiChatProvider">
        Provider<span class="text-danger">&nbsp;*</span>
      </label>
      <multiselect
        id="aiChatProvider"
        v-model="calculationData.provider"
        :options="aiOptions.calculationProviderOptions"
        :searchable="true"
        :close-on-select="true"
        :show-labels="false"
        :disabled="!aiOptions.calculationProviderOptions.length"
        placeholder="Select AI calculation provider"
      />
    </div>
    <div>
      <label for="aiChatModel">
        Model<span class="text-danger">&nbsp;*</span>
      </label>
      <multiselect
        id="aiChatModel"
        v-model="calculationData.providerModel"
        :options="calculationModelOptions"
        :searchable="true"
        :close-on-select="true"
        :show-labels="false"
        :disabled="!calculationModelOptions.length"
        placeholder="Select AI calculation model"
      />
    </div>
    <div>
      <label for="aiChatResponseType">
        Response Type<span class="text-danger">&nbsp;*</span>
      </label>
      <multiselect
        id="aiChatResponseType"
        v-model="calculationData.responseType"
        :options="aiOptions.calculationResponseTypeOptions"
        :searchable="true"
        :close-on-select="true"
        :show-labels="false"
        :disabled="!aiOptions.calculationResponseTypeOptions.length"
        placeholder="Select AI calculation response type"
      />
    </div>
    <div>
      <label for="aiChatIncludeCitations">
        Options
      </label>
      <b-form-checkbox
      id="aiChatIncludeCitations"
        v-model="calculationData.includeCitations"
        class="include_citations"
      >
        Include Citations
      </b-form-checkbox>
    </div>
    <b-form-group
      class="question_row"
      label="Fields"
    >
      <div class="search__wrapper">
        <b-input
          v-model="fieldSearch"
          placeholder="Search"
          debounce="300"
        />
        <b-button
          v-if="fieldSearch.length"
          variant="outline"
          size="sm"
          @click="fieldSearch = ''"
        >
          <font-awesome-icon :icon="['fas', 'close']" />
        </b-button>
      </div>

      <b-list-group class="group__item">
        <b-list-group-item
          v-for="item in fieldsToRender"
          :key="item.id"
          class="list__item-ai-calculation"
          :active="selectedField === item"
          @click="setSelectedField(item)"
          @dblclick="addFieldToQuestion(item)"
        >
          {{ item.text }}
        </b-list-group-item>
      </b-list-group>
      <b-button
        id="addCalculatedItemButton"
        size="sm"
        :disabled="isAddFieldButtonDisabled"
        block
        @click="addFieldToQuestion()"
      >
        <font-awesome-icon :icon="['fas', 'plus']" />
        Add Field
      </b-button>
    </b-form-group>
    <b-form-group
      class="question_row"
    >
      <template #label for="aiChatQuestion">
        Question<span class="text-danger">&nbsp;*</span>
      </template>
      <b-textarea
        id="aiChatQuestion"
        ref="aiTextarea"
        v-model="questionText"
        placeholder="Question for AI"
        @input="formatQuestion"
      />
    </b-form-group>
  </div>
</template>

<script>

  import {createNamespacedHelpers} from 'vuex';
  const {
    mapState: dataManagementMapState,
    mapActions: dataManagementMapActions,
  } = createNamespacedHelpers('dataManagement');

  export default {

    name: 'AIChatPanel',

    components: {
    },

    inject:[
      /* Current Table. [from CalculationModel] */
      'table',
      /* Current Field. [from CalculationModel] */
      'tableField',
      /* Calculation data - to be populated with Lookup Pairs. [from CalculationModel] */
      'calculationData',
      /* Entity utility functions etc. [from CalculationModel] */
      'entityUtils',
    ],

    props: {
    },

    data(){
        return {
          /** Raw question text as entered/displayed on control. */
          questionText: '',
          /** List of fields on this table: id=guid, text=name. */
          tableFields: [],
          /** Field {id,text} selected in list. */
          selectedField: {},
          /** User entered value to filter field list. */
          fieldSearch: '',
        };
    },

    computed: {
      ...dataManagementMapState({
        aiOptions: state => state.fieldOptions,
      }),
      calculationModelOptions() {
      if (!this.calculationData.provider) return [];
      return this.aiOptions.modelOptions.get(this.calculationData.provider);
    },
    isAddFieldButtonDisabled() {
      return !this.selectedField;
    },
    fieldsToRender() {
      if(this.fieldSearch.length) {
        return this.tableFields.filter(x => x.text.toLowerCase().includes(this.fieldSearch.toLowerCase()));
      }
      return this.tableFields;
    },
    },

    methods: {

      /* getFieldOptions - Get AI Calculation options from Data Management State. */
      ...dataManagementMapActions(['getFieldOptions']),

      /**
       * User has selected a field in the list.
       */
      setSelectedField(field) {
        this.selectedField = field === this.selectedField
          ? null
          : field;
      },

      /**
       * Format the field value (guid or name) to insert into the Question.
       * - Value is delimited with double squiggly brackets: {{...}}.
       * @param {String} field
       * @returns Field value (guid or name) formatted for inclusion in the Question.
       */
      formatFieldForQuestion(field) {
        return `{{${field}}}`;
      },

      /**
       * Insert the selected field in the Question.
       */
      addFieldToQuestion(item = null) {

        if (item) this.selectedField = item;
        if (!this.selectedField) return;

        /* Position of cursor / selected text in Question control. */
        const textarea = this.$refs.aiTextarea.$el;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;

        const textBefore =  this.questionText.slice(0, start) ;
        const textAfter = this.questionText.slice(end) ;
      
        this.questionText = textBefore + this.formatFieldForQuestion(this.selectedField.text) + textAfter;

        this.$nextTick(() => {
          textarea.selectionStart = textarea.selectionEnd = start + this.selectedField.text.length + 4;
          textarea.focus();
        });

        this.formatQuestion();
      },

      /**  */
      formatQuestion() {

        if (!this.questionText) {
          this.calculationData.question = '';
          return;
        }

        // Format the question so that the Field placeholders({{...}}) have Field Name rather than Guid.
        this.questionText = this.questionText.replaceAll(/{{+(.*?)}}+/g, (_, fld) => {
          /* Lookup the field by Guid - Question has been populated from Calculation Data. */
          let field = this.tableFields.find(x => x.id === fld);        
          if (field) return this.formatFieldForQuestion(field.text);
          /* Lookup Field by Name - Question has been populated from User input. */
          field = this.tableFields.find(x => x.text === fld);     
          //TODO: need to check with BE Dev
          return field ? this.formatFieldForQuestion(field.text) : '"non-existent field"';
        });

        /** Copy the Question to the Calculation Data, replacing field names with the corresponding Guid. */
        this.calculationData.question = this.questionText.replaceAll(/{{(.*?)}}/g, (_, fld) => {
          /* Lookup Field by Name. */
          const field = this.tableFields.find(x => x.text === fld);
          return this.formatFieldForQuestion(field.id);
        });
      },

    },

    /** Component's Reactive State is initialised but DOM not yet rendered. */
    beforeMount() {

      /* Initialise list of fields on this table for selection and addition to question. */
      this.tableFields = this.table.fields
        /* Ignore Current Field. */
        .filter((fld) => { return fld.fieldGuid !== this.tableField.fieldGuid; } )
        /* Convert to { id: guid, text: name } */
        .map((fld) => { return { id: fld.fieldGuid, text: fld.displayName }});

      /* Ensure that Calculation data is initialised. */
      if (!this.calculationData.hasOwnProperty('includeCitations')) this.$set(this.calculationData, 'includeCitations', true);
      if (!this.calculationData.hasOwnProperty('provider')) this.$set(this.calculationData, 'provider', '');
      if (!this.calculationData.hasOwnProperty('providerModel')) this.$set(this.calculationData, 'providerModel', '');
      if (!this.calculationData.hasOwnProperty('question')) this.$set(this.calculationData, 'question', '');
      if (!this.calculationData.hasOwnProperty('responseType')) this.$set(this.calculationData, 'responseType', '');

      /*
        Assign and format Question for edit/display.
        NB Must be done after tableFields is initialised.
      */
      this.questionText = this.calculationData.question;
      this.formatQuestion();

    },

    /** Component's Reactive State is initialised and DOM has been rendered. */
    async mounted() {

      /* getFieldOptions - Get AI Calculation options from Data Management State. */
      await this.getFieldOptions();

      /* Automatically select the first option for Provider, Model and Response type if available. */
      if (this.aiOptions.calculationProviderOptions.length) {
        this.calculationData.provider = this.aiOptions.calculationProviderOptions[0];
      }
      if (this.calculationModelOptions.length) {
        this.calculationData.providerModel = this.calculationModelOptions[0];
      }
      if (this.aiOptions.calculationResponseTypeOptions.length) {
        this.calculationData.responseType = this.aiOptions.calculationResponseTypeOptions[0];
      }

    },

  }

</script>


<style scoped lang="scss">

  .ai-chat__wrapper {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr auto;
    gap: 10px;
    
    & > :nth-child(5),
    & > :nth-child(6) {
      align-self: start;
      height: 300px;
    }
  }

  .question_row {
    margin-bottom: 0;

    :deep(& > div)  {
      height: 100%;

      .list-group {
        padding: 2px;
        margin-top: 5px;
        border: 1px solid #ccc;
        border-top-right-radius: 6px;
        border-top-left-radius: 6px;
        height: 200px;
        overflow-y: auto;
      }
    }

    #addCalculatedItemButton {
      margin-top: 5px;
    }

    textarea {
      resize: none;
      height: 100%;
    }
  }

  label {
    margin-bottom: 0.5rem;
  }

  .list__item-ai-calculation {
    padding: 5px;
  }
</style>